import { Camera, CameraOff, QrCode } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, CardBody, CardHeader, Field, Input, Notice, PageHeader } from '../components/ui';

/** QR-Inhalt (".../a/IT-000123") oder reine Inventarnummer → Asset-Referenz. */
export const refFromScan = (raw: string): string | null => {
  const text = raw.trim();
  if (!text) return null;
  const m = /\/a\/([^/?#\s]+)/.exec(text);
  if (m?.[1]) return decodeURIComponent(m[1]);
  if (/^https?:\/\//i.test(text)) return null;
  return text;
};

type Detector = { detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> };
type DetectorCtor = new (opts: { formats: string[] }) => Detector;

export function ScanPage() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const Ctor = (globalThis as unknown as { BarcodeDetector?: DetectorCtor }).BarcodeDetector;

  useEffect(() => {
    if (!active || !Ctor) return;
    let stream: MediaStream | null = null;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const detector = new Ctor({ formats: ['qr_code', 'code_128', 'code_39', 'data_matrix'] });

    const tick = async () => {
      if (stopped || !videoRef.current) return;
      try {
        const codes = await detector.detect(videoRef.current);
        const ref = codes.map((c) => refFromScan(c.rawValue)).find(Boolean);
        if (ref) {
          navigate(`/assets/${encodeURIComponent(ref)}`);
          return;
        }
      } catch {
        /* Bild noch nicht bereit */
      }
      timer = setTimeout(tick, 250);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(async (s) => {
        stream = s;
        if (stopped) return s.getTracks().forEach((t) => t.stop());
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          await videoRef.current.play();
        }
        void tick();
      })
      .catch(() => {
        setError('Kamera nicht verfügbar oder Zugriff verweigert.');
        setActive(false);
      });

    return () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [active, Ctor, navigate]);

  return (
    <>
      <PageHeader title="Scannen" subtitle="QR-Code eines Etiketts scannen oder Inventarnummer eingeben" />
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Kamera" />
          <CardBody className="space-y-3">
            {!Ctor ? (
              <Notice>
                Dieser Browser kann QR-Codes nicht direkt lesen (z. B. Firefox, Safari am Desktop). Tipp: Mit der Handy-Kamera den QR-Code scannen – der Link öffnet das Asset
                direkt. Oder rechts die Nummer eingeben.
              </Notice>
            ) : (
              <>
                <div className="relative aspect-video overflow-hidden rounded-md bg-slate-900">
                  <video ref={videoRef} className={active ? 'size-full object-cover' : 'hidden'} muted playsInline />
                  {!active && (
                    <div className="flex size-full items-center justify-center text-slate-400">
                      <QrCode className="size-16" />
                    </div>
                  )}
                  {active && <div className="pointer-events-none absolute inset-[20%] rounded-lg border-2 border-white/70" />}
                </div>
                <Button
                  variant={active ? 'secondary' : 'primary'}
                  icon={active ? <CameraOff className="size-4" /> : <Camera className="size-4" />}
                  onClick={() => {
                    setError(null);
                    setActive((a) => !a);
                  }}
                >
                  {active ? 'Kamera stoppen' : 'Kamera starten'}
                </Button>
                {error && <p className="text-sm text-red-600">{error}</p>}
              </>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Manuell" />
          <CardBody>
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const ref = refFromScan(manual);
                if (ref) navigate(`/assets/${encodeURIComponent(ref)}`);
              }}
            >
              <Field label="Inventarnummer" className="flex-1" hint="Auch Handscanner (Tastatur-Modus) funktionieren hier.">
                {(p) => <Input {...p} value={manual} onChange={(e) => setManual(e.target.value)} placeholder="IT-000123" autoFocus />}
              </Field>
              <Button type="submit" variant="primary" className="mb-5">
                Öffnen
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
